import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, LogOut, User as UserIcon } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/cn';

/**
 * Desktop uchun foydalanuvchi menyusi: profil va chiqish.
 * Mobil versiyada bu MobileMenu ichida beriladi.
 */
export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Profil sahifasi himoyalanmagan — chiqqandan keyin unda qolib ketmaslik uchun
  // bosh sahifaga o'tkazamiz.
  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/');
  };

  return (
    // Mobil versiyada chiqish MobileMenu ichida beriladi — bu yerda takrorlamaymiz.
    <div className="relative hidden md:block" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn btn-secondary ml-1 gap-2 rounded-DEFAULT px-3 py-2 text-sm"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <UserIcon className="h-4 w-4" />
        <span className="hidden sm:inline">{user?.username}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-lg border border-line bg-elevated py-1 shadow-lg animate-fade-in-fast"
          role="menu"
        >
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-content-secondary transition-colors hover:bg-surface hover:text-content"
          >
            <UserIcon className="h-4 w-4" />
            {t('nav.profile')}
          </Link>
          <button
            role="menuitem"
            onClick={onLogout}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-content-secondary transition-colors hover:bg-surface hover:text-content"
          >
            <LogOut className="h-4 w-4" />
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
